import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import DefaultApiLimit from '../../../../config/defaultLimit';
import {
  ConsumerReservationsInterFace,
  RequestConsumerReservationsInterFace,
} from '../../../../interface/end-consumer-reservations-interface';
import { useAppDispatch, useAppSelector } from '../../../../redux/hooks';
import { setSelectedDevice } from '../../../../redux/slices/DeviceManagementSlice';
import {
  fetchConsumerReservations,
  fetchConsumerReservationsFilter,
  setSortData,
  updateConsumerReservationsListFilter,
} from '../../../../redux/slices/EndConsumerReservations';
import { setNotificationMessage } from '../../../../redux/slices/NotificationManagementSlice';
import CustomTable from '../../../../shared-components/custom-table/CustomTable';
import DeviceSidePanel from '../../../../shared-components/table-side-panel/DeviceSidePanel';
import SidePaneModel from '../../../../shared-components/table-side-panel/SidePaneModel';
import StationSidePanel from '../../../../shared-components/table-side-panel/StationSidePanel';
import { GetParametersFromCommonFilters } from '../../../../shared-functions/CommonFilters';
import ConvertUTCtoDate from '../../../../shared-functions/ConvertUTCtoDate';
import { convertUtcTo12HourTime } from '../../../../shared-functions/DateTime';
import { exportCSV, exportPDF } from '../../../../shared-functions/ExportFile';
import endConsumerApi from '../../../../utils/api/end-consumer-v2';

interface ModelDetailsInterFace {
  title?: string;
  content: JSX.Element;
  url?: string;
  urlNewTab?: string;
}

/**
 * Charge Session List
 * @returns {React.ReactNode} - The rendered reservations list component.
 */
function ReservationsList() {
  const { t, i18n } = useTranslation('endConsumer');
  const dispatch = useAppDispatch();
  const [hiddenColumns, setHiddenColumns] = useState<string[]>([]);
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [modelDetails, setModelDetails] = useState<ModelDetailsInterFace>({
    content: <div />,
    url: '',
    urlNewTab: '',
  });
  const [exportLoader, setExportLoader] = useState<boolean>(false);
  const {
    consumers,
    limit,
    page,
    totalPages,
    totalResults,
    loading,
    filterListLoading,
    filters,
    isFilterUpdated,
    sortField,
    sortBy,
  } = useAppSelector((state) => state.ConsumerReservations);
  const { selectedEndConsumer, endConsumerType } = useAppSelector((state) => state.endConsumerType);

  /**
   * Fetch Users list function
   */
  const fetchData = (data: RequestConsumerReservationsInterFace) => {
    if (!loading && selectedEndConsumer) {
      dispatch(
        fetchConsumerReservations({
          userCustomId: selectedEndConsumer.userCustomId,
          ...data,
        }),
      );
    }
  };

  /**
   * Handle open station details
   * @param data - {ConsumerReservationsInterFace}
   */
  const handleOpenStationDetails = (data: ConsumerReservationsInterFace) => {
    navigate(`?stationId=${data?.chargingStation}`);
    setIsOpen(true);
    setModelDetails({
      content: <StationSidePanel fetchIdFrom="queryParams" />,
      url: `/ev-admin/end-consumer/${endConsumerType}/profile/${selectedEndConsumer?.userCustomId}/station/${data?.chargingStation}/station-dashboard?searchBy=${data?.chargingStationName}`,
    });
  };

  /**
   * Handle open station details
   * @param data - {ConsumerReservationsInterFace}
   */
  const handleOpenDeviceDetails = (data: ConsumerReservationsInterFace) => {
    navigate(`?deviceId=${data?.chargingPointSn}`);
    setIsOpen(true);
    dispatch(
      setSelectedDevice({
        id: data?.chargingPointId,
        name: data?.chargingPointName,
        chargingPointSn: data?.chargingPointSn,
      }),
    );
    setModelDetails({
      content: <DeviceSidePanel fetchIdFrom="queryParams" />,
      url: `/ev-admin/end-consumer/${endConsumerType}/profile/${selectedEndConsumer?.userCustomId}/device/${data?.chargingPointId}/device-details?searchBy=${data?.chargingPointName}`,
      urlNewTab: `/ev-admin/end-consumer/${endConsumerType}/profile/${selectedEndConsumer?.userCustomId}/device-expanded/${data?.chargingPointId}/device-details?searchBy=${data?.chargingPointName}`,
    });
  };

  const column = [
    {
      label: t('bookingDateTime'),
      accessorKey: 'startDateUtc' as keyof ConsumerReservationsInterFace,
      sortAllow: true,
      /**
       * Rendering booking date and time
       * @param {ConsumerReservationsInterFace} currentRow - current row data
       * @returns {React.ReactNode} - custom data
       */
      customData: (currentRow: ConsumerReservationsInterFace) => (
        <div>
          <p>{ConvertUTCtoDate(currentRow?.startDateUtc)}</p>
          <p>{convertUtcTo12HourTime(currentRow?.startDateUtc)}</p>
        </div>
      ),
    },
    {
      label: t('station'),
      accessorKey: 'chargingStationName' as keyof ConsumerReservationsInterFace,
      sortAllow: false,
      /**
       * Rendering station name as hyper link
       * @param {ConsumerReservationsInterFace} currentRow - current row data
       * @returns {React.ReactNode} - rendered station name
       */
      customData: (currentRow: ConsumerReservationsInterFace) => (
        <p
          role="presentation"
          className="hyper-link"
          onClick={() => handleOpenStationDetails(currentRow)}>
          {currentRow.chargingStationName}
        </p>
      ),
    },
    {
      label: t('device'),
      accessorKey: 'chargingPointSn' as keyof ConsumerReservationsInterFace,
      sortAllow: false,
      /**
       * Rendering device name as hyper link
       * @param {ConsumerReservationsInterFace} currentRow - current row data
       * @returns {React.ReactNode} - rendered device name
       */
      customData: (currentRow: ConsumerReservationsInterFace) => (
        <p
          role="presentation"
          className="hyper-link"
          onClick={() => handleOpenDeviceDetails(currentRow)}>
          {currentRow.chargingPointSn}
        </p>
      ),
    },
    {
      label: t('vehicle'),
      accessorKey: 'vehicleManufacturer' as keyof ConsumerReservationsInterFace,
      sortAllow: false,
    },
    {
      label: t('status'),
      accessorKey: 'status' as keyof ConsumerReservationsInterFace,
      sortAllow: false,
      /**
       * Rendering status with color text
       * @param {ConsumerReservationsInterFace} currentRow - current row data
       * @returns {React.ReactNode} - rendered status
       */
      customData: (currentRow: ConsumerReservationsInterFace) => {
        let statusClass = '';
        if (currentRow.status === 'cancelled') statusClass = 'cancelled';
        return <p className={statusClass}>{currentRow.status ? t(currentRow.status) : ''}</p>;
      },
    },
  ];

  /**
   * Current columns for Reservation export
   */
  const currentColumns = [
    { label: t('sNo'), accessorKey: '__serial__' },
    { label: t('bookingDateTime'), accessorKey: 'startDateUtc' },
    { label: t('station'), accessorKey: 'chargingStationName' },
    { label: t('device'), accessorKey: 'chargingPointSn' },
    { label: t('vehicle'), accessorKey: 'vehicleManufacturer' },
    { label: t('status'), accessorKey: 'status' },
  ];

  /**
   * Filtering columns for export without hidden columns
   */
  const exportCurrentColumns = currentColumns.filter(
    (col) => col.accessorKey === '__serial__' || !hiddenColumns.includes(col.accessorKey),
  );

  /**
   * Export data function
   */
  const exportDataPdfOrCSV = (type: string) => {
    setExportLoader(true);

    const queryParameters = GetParametersFromCommonFilters(filters);
    const payload: any = { ...queryParameters };

    if (sortField) payload.sortField = sortField;
    if (sortBy) payload.sortBy = sortBy;

    if (!selectedEndConsumer?.userCustomId) return;

    endConsumerApi
      .reservationExport(selectedEndConsumer.userCustomId, payload)
      .then((response) => {
        /**
         * Dynamic Headers
         */
        const headers = exportCurrentColumns.map((col) => col.label);

        /**
         * Dynamic Rows
         */
        const rows = response.data.map((row: ConsumerReservationsInterFace, index: number) =>
          exportCurrentColumns.map((col) => {
            switch (col.accessorKey) {
              case '__serial__':
                return index + 1;

              case 'startDateUtc':
                return row?.startDateUtc
                  ? `${ConvertUTCtoDate(
                      row.startDateUtc,
                    )} ${convertUtcTo12HourTime(row.startDateUtc)}`
                  : t('na');

              case 'chargingStationName':
                return row?.chargingStationName || t('na');

              case 'chargingPointSn':
                return row?.chargingPointSn || t('na');

              case 'vehicleManufacturer':
                return row?.vehicleManufacturer || t('na');

              case 'status':
                return row?.status ? t(row.status) : t('na');

              default:
                return (row as any)[col.accessorKey] || t('na');
            }
          }),
        );

        if (rows.length > 0) {
          if (type === 'csv') {
            exportCSV(headers, rows, 'end-consumer-reservations.csv');
          } else {
            exportPDF(headers, rows, 'end-consumer-reservations.pdf');
          }
        }

        setExportLoader(false);
      })
      .catch((error) => {
        setExportLoader(false);
        dispatch(
          setNotificationMessage({
            message: error?.response?.data?.message,
            status: true,
            type: 'error',
            code: error?.response?.data?.code,
          }),
        );
      });
  };

  /**
   * Table pagination function
   * @param {any} value - pagination value
   */
  const pageNateLimitChange = (value: any) => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    fetchData({ ...queryParameters, ...value, sortField, sortBy });
  };

  /**
   * Handle the filter data clear function
   */
  const handleSortFunction = (sortData: any) => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    dispatch(setSortData(sortData));
    fetchData({
      ...queryParameters,
      sortBy: sortData.sortBy,
      sortField: sortData.sortField,
      page: DefaultApiLimit.page,
      limit,
    });
  };

  /**
   * Function to refresh data
   */
  const handleRefresh = () => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    fetchData({ ...queryParameters, page, limit, sortField, sortBy });
  };

  /**
   * Handle the close function
   */
  const handleClose = () => {
    setIsOpen(false);
    setModelDetails({
      content: <div />,
      url: '',
    });
  };

  useEffect(() => {
    if (!selectedEndConsumer?.userCustomId) return;

    const currentId = selectedEndConsumer.userCustomId;
    const lastId = sessionStorage.getItem('reservationsEndConsumer');

    if (isFilterUpdated || consumers.length === 0 || lastId !== currentId) {
      if (selectedEndConsumer?.userCustomId) {
        const queryParameters = GetParametersFromCommonFilters(filters);
        fetchData({
          page: DefaultApiLimit.page,
          limit,
          ...queryParameters,
        });
      }
    }
    sessionStorage.setItem('reservationsEndConsumer', currentId);
  }, [filters, selectedEndConsumer]);

  return (
    <div dir={i18n.dir()} className="reservations-list-wrapper">
      <SidePaneModel
        open={isOpen}
        content={modelDetails.content}
        closePanel={handleClose}
        url={modelDetails.url}
        urlNewTab={modelDetails.urlNewTab}
      />
      <CustomTable
        header={column}
        data={consumers}
        serialNo
        actionButton
        sizeChangeButtonRequired
        version="v2"
        height={window.innerHeight >= 700 ? 325 : 329}
        page={page}
        limit={limit}
        loading={loading}
        totalPages={totalPages}
        totalResults={totalResults}
        sortData={{ sortBy, sortField }}
        handleSortChange={handleSortFunction}
        pageLimitChange={pageNateLimitChange}
        filters={filters}
        filtersToDispatch={updateConsumerReservationsListFilter}
        filterListLoading={filterListLoading}
        filterListToDispatch={fetchConsumerReservationsFilter}
        exportData={consumers}
        exportLoader={exportLoader}
        exportDataPdfOrCSV={exportDataPdfOrCSV}
        refreshRequired
        ReferenceCurrentPages={handleRefresh}
        leftTopContent={<> </>}
        expandTableOpen
        expandTableOpenKey="ReservationsV2ListEndConsumer"
        onHiddenColumnsChange={setHiddenColumns}
      />{' '}
    </div>
  );
}

export default ReservationsList;
